













































import Component from "vue-class-component";
import { getModule } from "vuex-module-decorators";
import { Watch } from "vue-property-decorator";
import Dinero from "dinero.js";
import Moment from "moment";
import { Base64 } from "js-base64";

import BaseComponent from "@/components/base-component.vue";
import NavigationDrawer from "@/components/navigation-drawer/navigation-drawer.vue";
import AppBarDefault from "@/components/app-bar-default/app-bar-default.vue";
import RrNotifications from '@/components/notifications/notifications.vue';
import ErrorSnackbar from "@/components/error-snackbar/error-snackbar.vue";
import Footer from "@/components/footer/footer.vue";
import CookieGDPR from "@/components/cookie-gdpr/cookie-gdpr.vue";
import { SettingsState } from "@/store/modules/settings";

@Component({
  name: "App",
  components: {
    NavigationDrawer,
    AppBarDefault,
    RrNotifications,
    ErrorSnackbar,
    Footer,
    CookieGDPR,
  },
})
export default class App extends BaseComponent {

  private readonly settingsState: SettingsState = getModule(SettingsState, this.$store);

  loadingDone: boolean = false;

  sng: string[] = ['az', 'hy', 'be', 'kk', 'ky', 'ro', 'tg', 'uz', 'uk', 'tk'];

  iconsContext: any = require.context(
    '!svg-inline-loader?'
    + 'removeTags=true&removeSVGTagAttrs=true&removingTagAttrs=fill'
    + '!../src/assets/icons',
    false,
    /\w+\.svg$/i,
  );

  showNotifications: boolean = true;

  get theme(): any {
    return this.settingsState.theme;
  }

  get themeDone(): boolean {
    return this.settingsState.themeDone;
  }

  get currentRoute(): any {
    return this.$route;
  }

  get currentRouteName(): string {
    return this.currentRoute ? this.currentRoute.name || '' : '';
  }

  get isBarHidden(): boolean {
    return (this.isModalActive && (this.smOnly || this.mdOnly))
      || (this.isAppBarHidden && (!this.smOnly || this.smOnly && this.$route.name !== 'search-from-url'));
  }

  get isAppBarHidden(): boolean {
    return this.settingsState.isAppBarHidden;
  }

  get isModalActive(): boolean {
    return this.settingsState.isModalActive;
  }

  get isLoadingEvents(): boolean {
    return this.settingsState.isLoadingEvents;
  }

  get isLoadingRoute(): boolean {
    return this.settingsState.isLoadingRoute;
  }

  get lang(): string {
    return this.settingsState.lang;
  }

  get languages(): string[] {
    return this.settingsState.languages;
  }

  get isAuthenticated() {
    return this.settingsState.isAuthenticated;
  }

  get email() {
    return this.settingsState.email;
  }

  get features(): any {
    return this.settingsState.features;
  }

  get showCookieGDPR(): boolean {
    return this.features ? this.features.includes('cookieGDPR') : false;
  }

  get showFooter(): boolean {
    return this.loadingDone && this.themeDone && !this.isLoadingEvents && !this.isLoadingRoute
      && (this.currentRoute ? this.currentRoute.name : false);
  }

  get isContacts(): boolean {
    return this.currentRoute.name === 'contacts';
  }

  get showCustomPage(): boolean {
    return [''].includes(this.currentRouteName);
  }

  get customPage(): string {
    return `/pages/${this.currentRouteName}/${this.lang || this.settingsState.defaultLanguage}/`;
  }

  async onSetUser() {
    if (this.isAuthenticated) {
      const email = this.email || '';
      if (email) await this.settingsState.setUser(email);
      return;
    }
    const email: any = this.$route.query.email || '';
    if (email) await this.settingsState.setUser(email);
  }

  onResize() {
    const root: any = this.$root;
    root.isMobile = window.innerWidth < 768;
    root.isTablet = window.innerWidth >= 768 && window.innerWidth < 1200;
    root.isDesktop = window.innerWidth >= 1200;
  }

  @Watch("themeDone")
  async onThemeDone(): Promise<void> {
    await this.settingsState.loadImages();
    await this.onSetUser();
    this.onResize();
  }

  @Watch("isLoadingEvents")
  async onLoadingEvents(): Promise<void> {
    if (this.isLoadingEvents) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }

  @Watch("lang")
  async onLangChanged(): Promise<void> {
    await this.changeLocale();
    await this.settingsState.loadImages();
  }

  @Watch("currentRouteName")
  async onRouteChanged(): Promise<void> {
    if (this.currentRouteName) {
      this.showNotifications = false;
      setTimeout(() => { this.showNotifications = true; }, 100);

      if (this.showCustomPage) {
        this.$store.state.buttonHome = true;
        this.$store.state.buttonBack = false;
        this.$store.state.buttonCart = true;
        this.$store.state.showMenu = true;

        window.scrollTo(0, 0);
        setTimeout(() => { this.disableVerticalScroll(true); }, 100);
      } else {
        this.disableVerticalScroll(false);
      }
    }
  }

  disableVerticalScroll(disable: boolean) {
    if (disable) {
      document.documentElement.setAttribute('style', 'overflow-y: hidden !important;');
      document.body.style.overflowY = 'hidden';
    } else {
      document.documentElement.setAttribute('style', '');
      document.body.style.overflowY = '';
    }
  }

  decodeRecentSearch(value: string): any[] {
    const pre = this.parseRecentSearch(value);
    if (pre.length > 0) return pre;

    let decoded = '';

    try {
      decoded = Base64.decode(value);
    } catch (e) {
      return [];
    }

    const parsed = this.parseRecentSearch(decoded);

    return parsed;
  }

  encodeRecentSearch(value: any[]): string {
    let encoded = '';
    try {
      encoded = Base64.encode(JSON.stringify(value));
    } catch (e) {
      return '';
    }

    return encoded;
  }

  parseRecentSearch(decoded: string): any[] {
    let parsed = [];
    try {
      parsed = JSON.parse(decoded);
    } catch (e) {
      return [];
    }

    return parsed;
  }

  async detectBrowserLang(): Promise<string> {
    const detected: string = window.navigator.language?.slice(0, 2);
    if (detected !== this.settingsState.defaultLanguage) {
      if (this.languages.includes(detected)) {
        return detected;
      } else if (this.sng.includes(detected) && this.languages.includes('ru')) {
        return 'ru';
      } else {
        return 'en';
      }
    }
    return this.settingsState.defaultLanguage;
  }

  async changeLocale(): Promise<void> {
    const lang: string = this.settingsState.lang || this.settingsState.defaultLanguage;
    this.$root.$i18n.locale = lang;
    this.$vuetify.lang.current = lang;
    Moment.locale(lang);
    Dinero.globalLocale = lang;
  }

  async created(): Promise<void> {
    await this.settingsState.loadSettings();
    await this.settingsState.loadLang();
    if (this.settingsState.isUsedDefaultLang) {
      const lang: string = await this.detectBrowserLang();
      if (lang) await this.settingsState.setLang(lang);
    }
    if (this.settingsState.languages && this.settingsState.languages.length === 1) {
      this.$store.state.showLang = false;
    }

    await this.changeLocale();
    const root: any = this.$root;
    root.$decodeRecentSearch = this.decodeRecentSearch;
    root.$encodeRecentSearch = this.encodeRecentSearch;
  }

  async mounted(): Promise<void> {
    this.loadingDone = true;

    window.addEventListener('resize', () => {
      this.onResize();
    });
    this.onResize();

    setTimeout(() => {
      if (this.themeDone) {
        this.onSetUser();
      }
    }, 1000);
  }

}
